import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Box, Link } from '@mui/material'

// Shared renderer for the admin-authored description / documentation text shown
// across the catalog. Content is treated as Markdown (bold, italics, lists,
// links, headings, …). react-markdown never renders raw HTML, so the input
// stays safe to display.
//
// Typography (colour, size, block spacing) is left to the caller via `sx`, so a
// description reads the same as the MUI Typography it replaces. Pass
// `disableLinks` when the block sits inside another clickable element (a catalog
// card) to avoid nesting anchors — links then render as plain underlined text.
function buildComponents(disableLinks) {
  return {
    a: disableLinks
      ? ({ node, children }) => (
          <Box component="span" sx={{ textDecoration: 'underline' }}>{children}</Box>
        )
      : ({ node, ...props }) => (
          <Link {...props} target="_blank" rel="noopener noreferrer" underline="hover" />
        ),
  }
}

export default function Markdown({ children, sx, disableLinks = false, ...boxProps }) {
  if (!children) return null

  return (
    <Box
      {...boxProps}
      sx={{
        // Collapse the default browser margins into tight spacing that matches
        // the surrounding Typography blocks.
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
        '& p': { my: 1 },
        // Tailwind's Preflight resets list markers to `none`; restore them so
        // bullets / numbers show on the public catalog.
        '& ul, & ol': { my: 1, pl: 3 },
        '& ul': { listStyleType: 'disc' },
        '& ol': { listStyleType: 'decimal' },
        '& li': { display: 'list-item', mb: 0.5 },
        // Tailwind's Preflight resets headings to `font-size: inherit`, so
        // restore a size scale (em-relative, to track the container's size).
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontWeight: 700, lineHeight: 1.3, mt: 1.5, mb: 0.5,
        },
        '& h1': { fontSize: '2em', paddingTop: '0.5em' },
        '& h2': { fontSize: '1.5em', paddingTop: '0.5em' },
        '& h3': { fontSize: '1.3em' },
        '& h4, & h5, & h6': { fontSize: '1em' },
        '& a': { color: 'primary.main' },
        '& code': {
          px: 0.5, py: '0.1em', borderRadius: 0.5,
          bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: '0.875em',
        },
        '& blockquote': {
          my: 0.5, ml: 0, pl: 1.5,
          borderLeft: '3px solid', borderColor: 'divider', color: 'text.secondary',
        },
        '& img': { maxWidth: '100%' },
        ...sx,
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={buildComponents(disableLinks)}>
        {children}
      </ReactMarkdown>
    </Box>
  )
}
